import type { GridApiPro, GridColDef, GridPaginationModel } from '@mui/x-data-grid-pro';
import type { CommitmentVsUsageReportItem, Period } from '@vy/types';
import { useCallback, useMemo, useState } from 'react';
import { VyTableLite } from '../../../../../components/new-design/table/vy-table/VyTable';
import type { FilterLineType } from '../../../../../components/new-design/vyTableFilter';
import { useCommitmentVsUsageReport } from '../../../../../hooks/requests/use-commitment-vs-usage-report';
import { useTableColumnsManager } from '../../../../../hooks/use-table-columns-manager';
import { useFilteredCommitmentVsUsage } from '../../hooks/use-filtered-commitment-vs-usage';
import { buildCommitmentVsUsageColumnGroups } from './CommitmentVsUsageReportTableColumns';

const TABLE_NAME = 'commitment-vs-usage';
const PAGE_SIZE_OPTIONS = [25, 50, 100, 500];
const DEFAULT_PAGINATION_MODEL: GridPaginationModel = { page: 0, pageSize: 100 };
const EMPTY_REPORT_ITEMS: CommitmentVsUsageReportItem[] = [];
const EMPTY_MONTHS: { startTime: Date }[] = [];

type Props = {
  period: Partial<Period>;
  searchText: string | null | undefined;
  filters: FilterLineType[];
  columns: GridColDef<CommitmentVsUsageReportItem>[];
  apiRef?: React.MutableRefObject<GridApiPro>;
};

export const CommitmentVsUsageReportTable: React.FC<Props> = ({
  period,
  searchText,
  filters,
  columns,
  apiRef,
}) => {
  const { data: report, isLoading } = useCommitmentVsUsageReport(period);
  const filteredReport = useFilteredCommitmentVsUsage(report, filters, searchText);

  const months = report?.months ?? EMPTY_MONTHS;
  const columnGroupingModel = useMemo(() => buildCommitmentVsUsageColumnGroups(months), [months]);

  const stableGetRowId = useCallback(
    (row: CommitmentVsUsageReportItem) =>
      `${row.contract.id}-${row.product.variantId}-${row.monthlyEntries[0]?.startTime.toISOString() ?? row.commitment.units ?? 'no-commitment'}`,
    [],
  );

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(DEFAULT_PAGINATION_MODEL);

  const { processedColumns } = useTableColumnsManager<CommitmentVsUsageReportItem>({
    apiRef,
    columns,
    tableName: TABLE_NAME,
  });

  return (
    <VyTableLite
      apiRef={apiRef}
      items={filteredReport?.reportItems ?? EMPTY_REPORT_ITEMS}
      columns={processedColumns}
      columnGroupingModel={columnGroupingModel}
      loadingItems={isLoading}
      getRowId={stableGetRowId}
      density="compact"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      pageSizeOptions={PAGE_SIZE_OPTIONS}
    />
  );
};
